import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SearchParams } from '@stylehaven/components';

import {
    ApiFragment,
    CustomErrorType,
    ExpectedVersion,
    PagedResult,
    RecordSecurityGroups,
    RecordService,
    SecurityGroup
} from '@digihaven/shared';

import { Extension } from '@common/entities/extensions/extension.entity';
import { SeriesCloneAction, SeriesCloneCreateResponse } from '@common/entities/series/series-clone.entity';
import { SeriesExtensionsUpdate } from '@common/entities/series/series-extensions.entity';
import { SeriesRelation, SeriesRelations } from '@common/entities/series/series-relations.entity';
import { SeriesSearch } from '@common/entities/series/series-search.entity';
import { SeriesSubmitValidation, SeriesValidation } from '@common/entities/series/series-validate.entity';
import { Series } from '@common/entities/series/series.entity';
import { AddSeriesRelation } from '@common/entities/series/types/add-series-relation.type';
import { TaskAction } from '@common/entities/task/task-action.entity';
import { TaskCreateAction } from '@common/entities/task/task-create.entity';
import { TaskVariable } from '@common/entities/task/task-variable.entity';
import { Task } from '@common/entities/task/task.entity';
import { Upload } from '@common/entities/upload/upload.entity';
import { GetLastModifiedDate } from '@common/helpers/record/get-last-modified-date.helper';
import { GetSeriesId } from '@common/helpers/record/get-record-id.helper';
import { GetRecordVersion } from '@common/helpers/record/get-record-version.helper';
import { WaitForSecurityGroupsUpdate } from '@common/helpers/wait-for-security-groups-update.helper';
import { HttpService } from '@common/services/http/http.service';
import { UploadSandboxService } from '@common/services/upload/upload.service';

@Injectable()
export class SeriesService {
    private readonly ENDPOINT: string = 'series';

    constructor(
        private httpService: HttpService,
        private UploadSandboxService: UploadSandboxService,
        private recordService: RecordService
    ) {}

    getSeries(id: string, seriesVersion?: string): Observable<Series> {
        const getSeries$ = seriesVersion
            ? this.httpService.get<Series>(`${this.ENDPOINT}/${id}/versions/${seriesVersion}`)
            : this.httpService.get<Series>(`${this.ENDPOINT}/${id}`);

        return getSeries$;
    }

    getSeriesBlob(id: string, seriesVersion?: string): Observable<HttpResponse<Blob>> {
        let endpoint = `${this.ENDPOINT}/${id}`;
        if (seriesVersion) {
            endpoint += `/versions/${seriesVersion}`;
        }

        return this.httpService.get<HttpResponse<Blob>>(
            endpoint,
            undefined,
            undefined,
            this.csvHeaders,
            'blob',
            'response'
        );
    }

    getTemplateBlob(): Observable<HttpResponse<Blob>> {
        return this.httpService.get<HttpResponse<Blob>>(
            `${this.ENDPOINT}/bulk/metadata-template`,
            undefined,
            undefined,
            this.excelHeaders,
            'blob',
            'response'
        );
    }

    getAllSeries(search?: SeriesSearch): Observable<PagedResult<Series>> {
        return this.httpService.get<PagedResult<Series>>(this.ENDPOINT, search as SearchParams);
    }

    getAllSeriesBlob(search?: SeriesSearch): Observable<HttpResponse<Blob>> {
        return this.httpService.get<HttpResponse<Blob>>(
            this.ENDPOINT,
            search as SearchParams,
            undefined,
            this.csvHeaders,
            'blob',
            'response'
        );
    }

    getAllSeriesBlobForOneOrganisation(search?: SeriesSearch): Observable<HttpResponse<Blob>> {
        return this.httpService.get<HttpResponse<Blob>>(
            `${this.ENDPOINT}/batch`,
            search as SearchParams,
            undefined,
            this.csvHeaders,
            'blob',
            'response'
        );
    }

    create(series: Series): Observable<Series> {
        return this.httpService.post<Series>(this.ENDPOINT, series).pipe(
            switchMap((createdSeries: Series) => {
                return this.recordService
                    .waitForRecordToBeIndexed(createdSeries.Id!)
                    .pipe(switchMap(() => this.getSeries(createdSeries.Id!)));
            })
        );
    }

    createDryRun(series: Series): Observable<Series> {
        return this.httpService.post<Series>(this.ENDPOINT, series, {
            observe: 'body',
            params: { dryRun: 'true' }
        }) as Observable<Series>;
    }

    save(series: Series, originalSeries?: Series): Observable<Series> {
        const saveStream$ = this.httpService.put<Series>(`${this.ENDPOINT}/${series.Id}`, series);

        if (originalSeries) {
            return saveStream$.pipe(
                switchMap(() => this.waitForUpdateToCurrentVersion(originalSeries)),
                switchMap(() => this.getSeries(originalSeries.Id))
            );
        } else {
            return saveStream$;
        }
    }

    saveDryRun(series: Series): Observable<Series> {
        return this.httpService.put<Series>(`${this.ENDPOINT}/${series.Id}`, series, undefined, { dryRun: 'true' });
    }

    delete(seriesId: string): Observable<void> {
        return this.httpService
            .delete<void>(`${this.ENDPOINT}/${seriesId}`)
            .pipe(switchMap(() => this.recordService.waitForRecordToBeRemovedFromIndex(seriesId)));
    }

    getTask(seriesId: string, taskId: string): Observable<Task> {
        return this.httpService.get<Task>(`${this.ENDPOINT}/${seriesId}/tasks/${taskId}`);
    }

    createTask(
        seriesId: string,
        taskCreateAction: TaskCreateAction,
        originalSeries?: Series | ApiFragment
    ): Observable<SeriesSubmitValidation> {
        const createTask$ = this.httpService.post<SeriesSubmitValidation>(
            `${this.ENDPOINT}/${seriesId}/tasks`,
            taskCreateAction
        );

        if (originalSeries) {
            return createTask$.pipe(
                switchMap(response => this.waitForUpdateToCurrentVersion(originalSeries).pipe(map(() => response)))
            );
        } else {
            return createTask$;
        }
    }

    createTaskFromRecord(
        record: ApiFragment | Series,
        taskCreateAction: TaskCreateAction
    ): Observable<SeriesSubmitValidation> {
        const seriesId: string = GetSeriesId(record);
        const createTask$ = this.httpService.post<SeriesSubmitValidation>(
            `${this.ENDPOINT}/${seriesId}/tasks`,
            taskCreateAction
        );

        return createTask$.pipe(
            switchMap(response => this.waitForUpdateToCurrentVersion(record).pipe(map(() => response)))
        );
    }

    submitTaskAction(
        seriesId: string,
        taskId: string,
        action: TaskAction,
        originalSeries?: Series | ApiFragment
    ): Observable<void> {
        const submitTask$: Observable<void> = this.httpService.post<void>(
            `${this.ENDPOINT}/${seriesId}/tasks/${taskId}/action`,
            action
        );

        if (originalSeries) {
            return submitTask$.pipe(
                switchMap(response => this.waitForUpdateToCurrentVersion(originalSeries).pipe(map(() => response)))
            );
        }

        return submitTask$;
    }

    saveTaskVariable(seriesId: string, taskId: string, taskVariable: TaskVariable): Observable<void> {
        return this.httpService.put<void>(
            `${this.ENDPOINT}/${seriesId}/tasks/${taskId}/variables/${taskVariable.Type}`,
            { Value: taskVariable.Value }
        );
    }

    getValidation(seriesId: string): Observable<SeriesValidation> {
        return this.httpService.post<SeriesValidation>(`${this.ENDPOINT}/${seriesId}/validations`, null);
    }

    getExtensions(seriesId: string, seriesVersion: number): Observable<Extension[]> {
        return this.httpService
            .get<Extension[]>(`${this.ENDPOINT}/${seriesId}/versions/${seriesVersion}/extensions`)
            .pipe(map(extensions => extensions.sort((a, b) => a.Name.localeCompare(b.Name))));
    }

    saveExtensions(
        seriesId: string,
        seriesVersion: number,
        seriesExtensions: SeriesExtensionsUpdate
    ): Observable<ExpectedVersion> {
        return this.httpService
            .put<SeriesExtensionsUpdate>(
                `${this.ENDPOINT}/${seriesId}/versions/${seriesVersion}/extensions`,
                seriesExtensions,
                true
            )
            .pipe(
                map(resp => ({
                    version: resp.headers.get('expect')
                })),
                switchMap(expectedVersion =>
                    this.recordService.waitForRecordToBeIndexed(seriesId, expectedVersion.version!).pipe(
                        catchError((err, o) => {
                            // Ignore IndexTimeout error (UI should still navigate to new version)

                            if (err === CustomErrorType.IndexTimeout) {
                                return of(expectedVersion);
                            }

                            return throwError(err);
                        }),

                        map(() => expectedVersion)
                    )
                )
            );
    }

    getSecurityGroups(seriesId: string, seriesVersion: number): Observable<RecordSecurityGroups> {
        return this.httpService.get<RecordSecurityGroups>(
            `${this.ENDPOINT}/${seriesId}/versions/${seriesVersion}/security-groups`
        );
    }

    saveSecurityGroups(
        seriesId: string,
        securityGroupIds: string[],
        originalSecurityGroups?: SecurityGroup[]
    ): Observable<ExpectedVersion> {
        const saveStream$ = this.httpService
            .put<RecordSecurityGroups>(
                `${this.ENDPOINT}/${seriesId}/security-groups`,
                { SecurityGroups: securityGroupIds },
                true
            )
            .pipe(
                map(resp => ({
                    version: resp.headers.get('expect')
                }))
            );

        if (originalSecurityGroups) {
            return saveStream$.pipe(
                switchMap(expectedVersion =>
                    WaitForSecurityGroupsUpdate(seriesId, expectedVersion.version!, originalSecurityGroups!, this).pipe(
                        map(securityGroup => expectedVersion)
                    )
                )
            );
        } else {
            return saveStream$;
        }
    }

    cloneSeries(
        seriesId: string,
        seriesVersion: string | number,
        action: SeriesCloneAction
    ): Observable<SeriesCloneCreateResponse> {
        return this.httpService
            .post<SeriesCloneCreateResponse>(`${this.ENDPOINT}/${seriesId}/versions/${seriesVersion}/clone`, action)
            .pipe(
                switchMap(cloneResponse =>
                    this.recordService.waitForRecordToBeIndexed(cloneResponse.Id).pipe(map(() => cloneResponse))
                )
            );
    }

    batchUpload(file: File): Observable<Upload> {
        return this.UploadSandboxService.upload(`${this.ENDPOINT}/bulk`, file);
    }

    cancelBatch(upload: Upload): void {
        return this.UploadSandboxService.cancelUpload(upload);
    }

    getVersions(seriesId: string): Observable<Series[]> {
        return this.httpService.get<Series[]>(`${this.ENDPOINT}/${seriesId}/versions`);
    }

    getRelations(seriesId: string, seriesVersion: number): Observable<SeriesRelations> {
        return this.httpService.get<SeriesRelations>(
            `${this.ENDPOINT}/${seriesId}/versions/${seriesVersion}/relations`
        );
    }

    addRelation(seriesId: string, relation: AddSeriesRelation, originalSeries?: Series): Observable<ExpectedVersion> {
        const addRelation$: Observable<ExpectedVersion> = this.httpService
            .post<SeriesRelation>(`${this.ENDPOINT}/${seriesId}/relations`, relation, {
                observe: 'response'
            })
            .pipe(
                map(resp => ({
                    version: resp.headers.get('expect')
                }))
            );

        if (originalSeries) {
            return addRelation$.pipe(
                switchMap(expectedVersion => this.waitForSeriesUpdateOrNewVersion(expectedVersion, originalSeries))
            );
        }

        return addRelation$;
    }

    deleteRelation(
        seriesId: string,
        relation: AddSeriesRelation,
        originalSeries?: Series
    ): Observable<ExpectedVersion> {
        const deleteRelation$: Observable<ExpectedVersion> = this.httpService
            .delete<void>(`${this.ENDPOINT}/${seriesId}/relations/${relation.Type}/${relation.Id}`, true)
            .pipe(
                map(resp => ({
                    version: resp.headers.get('expect')
                }))
            );
        if (originalSeries) {
            return deleteRelation$.pipe(
                switchMap(expectedVersion => this.waitForSeriesUpdateOrNewVersion(expectedVersion, originalSeries))
            );
        }

        return deleteRelation$;
    }

    private waitForSeriesUpdateOrNewVersion(expectedVersion: ExpectedVersion, originalSeries: Series) {
        const shouldCheckAgainstNewVersion: boolean = expectedVersion.version !== originalSeries.Version.toString();

        const seriesStream$ = shouldCheckAgainstNewVersion
            ? this.waitForUpdateToExpectedVersion(originalSeries, expectedVersion)
            : this.waitForUpdateToCurrentVersion(originalSeries);
        return seriesStream$.pipe(map(() => expectedVersion));
    }

    waitForUpdateToCurrentVersion(record: Series | ApiFragment) {
        const version: string = GetRecordVersion(record);
        return this.waitForUpdate(record, version);
    }

    private waitForUpdateToExpectedVersion(record: Series | ApiFragment, expectedVersion: ExpectedVersion) {
        return this.waitForUpdate(record, expectedVersion.version!);
    }

    private waitForUpdate(record: Series | ApiFragment, version: string): Observable<void> {
        const recordId: string = GetSeriesId(record);
        const lastModifiedDate: string = GetLastModifiedDate(record);
        return this.recordService.waitForRecordUpdate(recordId, version, lastModifiedDate);
    }

    private get csvHeaders(): HttpHeaders {
        return new HttpHeaders({
            Accept: ['text/csv', 'application/json'],
            'Content-Type': 'text/csv'
        });
    }

    private get excelHeaders(): HttpHeaders {
        return new HttpHeaders({
            Accept: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/json'],
            'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
    }
}
